import toNumber from 'lodash/toNumber';
import {
    INPUT_TYPE_ADDRESS,
    INPUT_TYPE_MULTI_SELECT,
    INPUT_TYPE_NUMBER,
    INPUT_TYPE_CHECKBOX,
} from 'common/entities/formNode/inputTypes';
import { NODE_TYPE_QUESTION, NODE_TYPE_QUESTION_MULTIPLE } from 'common/entities/formNode/nodeTypes';

import getNodesVisibility from './getNodesVisibility';

const FORM_FIELDS_TO_FORMAT = {
    'first-name': 'first_name',
    'last-name': 'last_name',
    email: 'email',
    attending: 'attending',
    'additional-guest-count': 'additional_guest_count',
};
/**
 * @param  {Object} params - object of pairs by name and value from the form.
 * @return {Object} - pairs updated. { [nodeName]: [value] }
 */
const composePayload = (params) => ({
    payload: JSON.stringify(params),
    action: 'rsvp',
    attending: typeof params.attending !== 'undefined' ? params.attending : 1,
});

/**
 * Get the attendee field name.
 * @param  {string} name - string containing the field name.
 * @return {string} - question name formatted.
 * Example. name = additional-guest[0]last-name -> returns last-name
 */
const getGuestFieldName = (name) => FORM_FIELDS_TO_FORMAT[name.substring(name.indexOf(']') + 1)];

/**
 * Get the attendee field name with its corresponding index.
 * @param  {string} name - string containing the field name.
 * @param  {string} attendeeNumber - string containing the guest number in the form.
 * @return {string} - question name formatted.
 */
const fillGuestField = (name, attendeeNumber) => {
    const field = `guest_${getGuestFieldName(name)}s`;
    return `${field}[${attendeeNumber}]`;
};

/**
 * Get the formatted field name.
 * @param  {Object} question - object with all question values.
 * @return {string} - question name formatted.
 */
const formatQuestionName = (question) => {
    if (/^additional-guest\[[0-9]+\]/.test(question.name)) {
        const attendeeNumber = question.name.replace(/\D+/g, '');
        return fillGuestField(question.name, attendeeNumber);
    }
    return FORM_FIELDS_TO_FORMAT[question.name];
};

/**
 * Get the formatted field name.
 * @param  {Object} question - question node.
 * @param  {Object} values - object with all values.
 * @return {Array} - values that passes the filter in an array.
 */
const formatObjectToString = (question, values) => {
    if (question.inputType === INPUT_TYPE_ADDRESS) {
        return values.label || '';
    } else if (question.inputType === INPUT_TYPE_MULTI_SELECT) {
        const objectsToFormat = Object.entries(values).filter(([key, value]) => value === true);
        return objectsToFormat.map((e) => e[0]);
    }
    return values;
};

/**
 * Gets the value corresponding on the node id, checks if the value is type object,
 * which will come with pairs of key true || false.
 * @param  {Object} question
 * @param  {*} value
 * @param  {bool} isVisible - string containing the node id.
 * @return {*} - value from the values object corresponding to the node.
 */
const parseQuestionValue = (question, value, isVisible) => {
    // Not visibile questions should always be `null`, meaning 'not replied'.
    if (!isVisible) {
        return null;
    }

    if (typeof value === 'object' && value !== null) {
        return formatObjectToString(question, value);
    }

    // Empty number fields submit "" (like text fields) so they render as
    // "No Response", not a stored 0. A genuinely typed 0 is not empty and falls
    // through to the block below, where it is preserved as 0.
    if (question.inputType === INPUT_TYPE_NUMBER && (value === undefined || value === null || value === '')) {
        return '';
    }

    // Parsing bool to int for now as well bc Cake's system interprets 0 as unchecked.
    if (question.inputType === INPUT_TYPE_NUMBER || question.inputType === INPUT_TYPE_CHECKBOX) {
        const conversionPerType = {
            number: (v) => v,
            boolean: (v) => Number(v),
            string: (v) => {
                const number = toNumber(v);
                return Number.isNaN(number) ? v : number; // optimistically send non number for now.
            },
            undefined: () => 0,
            object: (v) => (v === null ? 0 : 1),
            default: (v) => v,
        };
        const numericValue = (conversionPerType[typeof value] || conversionPerType.default)(value);
        return numericValue;
    }

    // Turn undefined to empty string
    if (value === undefined) {
        return '';
    }

    return value;
};

/**
 * This function get all key, value pairs from the node and values received.
 * @param  {Array} formNodes - array of entities, including nodes that might not be visible.
 * @param  {Array} formLogicRules
 * @param  {Object} values - { [nodeId]: [value] }
 * @return {Object} - pairs. { [nodeName]: [value] }
 */
export function getFormValuesByPair(formNodes, formLogicRules, values) {
    const formNodesVisibility = getNodesVisibility(formNodes, formLogicRules, values);

    const formQuestions = formNodes.filter(
        (node) => node.type === NODE_TYPE_QUESTION || node.type === NODE_TYPE_QUESTION_MULTIPLE,
    );
    const formSubmissionParams = formQuestions.reduce((prev, question) => {
        const value = parseQuestionValue(question, values[question.id], formNodesVisibility[question.id]);

        const questionNameForSubmission = formatQuestionName(question);
        if (questionNameForSubmission) {
            return {
                ...prev,
                [questionNameForSubmission]: value,
            };
        }

        return {
            ...prev,
            customNode: {
                ...(prev.customNode || {}),
                [question.id]: value,
            },
        };
    }, {});
    return formSubmissionParams;
}

/**
 * Creates a formatted submission payload for form data
 * @param  {Array} formNodes - array of entities, including nodes that might not be visible.
 * @param  {Array} formLogicRules
 * @param  {Object} values - { [nodeId]: [value] }
 * @param  {Array} sessions - optional array of session ids
 * @return {Object} - final payload
 */
export default function createSubmissionPayload(formNodes, formLogicRules, values, sessions) {
    const formSubmissionParams = getFormValuesByPair(formNodes, formLogicRules, values);

    return composePayload({
        ...formSubmissionParams,
        sessions: String(formSubmissionParams?.attending) === '0' ? undefined : sessions,
    });
}
